import React from 'react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';

const SectionHeading = ({ children }) => (
  <h2 className="font-serif-display text-2xl text-[#14213D] mt-10 mb-3">{children}</h2>
);

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 11, 2026">
      <p>
        Atlas History (&ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; or
        &ldquo;<strong>our</strong>&rdquo;) operates atlashistory.org (the &ldquo;Service&rdquo;).
        This Privacy Policy describes what information we collect from teachers, students, and
        administrators using the Service, how we use it, and the choices you have.
      </p>

      <SectionHeading>1. Information we collect</SectionHeading>
      <p><strong>Account information.</strong> When you create an account or sign in with Google,
        we collect:</p>
      <ul>
        <li>Your name and email address</li>
        <li>Your role (teacher, student, or administrator) and assigned grade level</li>
        <li>A bcrypt-hashed copy of your password (if you sign in with email/password — we never
          store passwords in readable form)</li>
        <li>Your Google profile picture URL (if you sign in with Google)</li>
      </ul>

      <p><strong>Usage information.</strong> We log routine information about how you use the
        Service to keep it running and to improve it:</p>
      <ul>
        <li>Which curriculum units, primary sources, and tools you open</li>
        <li>Items you pin to your Collection</li>
        <li>Annotations, quiz responses, and essay submissions you create</li>
        <li>Approximate IP address and browser type (in server logs, retained 30 days)</li>
      </ul>

      <p><strong>Payment information.</strong> If you subscribe to a paid plan, payment is
        processed by <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">Stripe</a>.
        Atlas History never sees or stores your full card number, CVV, or banking information.
        We receive only a transaction ID and the tier you purchased.</p>

      <SectionHeading>2. How we use your information</SectionHeading>
      <ul>
        <li>To provide and personalize the curriculum, tools, and features you use</li>
        <li>To authenticate sign-in and protect against fraud and abuse</li>
        <li>To send transactional emails (password resets, subscription receipts, admin alerts)</li>
        <li>To improve the Service — e.g., to identify broken links and improve search</li>
        <li>To comply with legal obligations</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal information. We do <strong>not</strong>{' '}
        share it with advertisers. We do <strong>not</strong> use your content to train AI
        models.
      </p>

      <SectionHeading>3. Third-party services we use</SectionHeading>
      <ul>
        <li><strong>Google OAuth</strong> — used only to verify your identity when you choose
          &ldquo;Sign in with Google.&rdquo; We request only your email, name, and profile picture.</li>
        <li><strong>Stripe</strong> — payment processor for subscriptions.</li>
        <li><strong>Resend</strong> — transactional email delivery (password resets, alerts).</li>
        <li><strong>Google Gemini</strong> — when you use the AI assistant features, your prompt
          and the relevant unit context are sent to Google&apos;s Gemini API to generate the
          response. Google does not retain this content for model training (per their API terms).</li>
        <li><strong>MongoDB</strong> — managed cloud database service that hosts our data.</li>
      </ul>

      <SectionHeading>4. Cookies and local storage</SectionHeading>
      <p>
        We use browser <code>localStorage</code> to keep you signed in across visits and to
        remember small preferences (such as your sidebar collapsed state). We do not use
        third-party advertising or tracking cookies.
      </p>

      <SectionHeading>5. Children&apos;s privacy (FERPA / COPPA)</SectionHeading>
      <p>
        Atlas History is designed for use in classrooms under a teacher&apos;s direction. Student
        accounts are created by a teacher or administrator on the student&apos;s behalf. We do
        not knowingly collect personal information directly from children under 13.
      </p>
      <p>
        Schools using Atlas History serve as the school official under the{' '}
        <strong>Family Educational Rights and Privacy Act (FERPA)</strong> when sharing
        directory information (student name + grade) with us, and may share or remove student
        data on request via the administrator.
      </p>

      <SectionHeading>6. Your rights and choices</SectionHeading>
      <p>You can:</p>
      <ul>
        <li>Update your name, email, or password from your profile</li>
        <li>Delete your account by emailing the address below; deletion is performed within 30 days</li>
        <li>Request a copy of your stored data, or ask that it be corrected</li>
        <li>Opt out of non-essential email (digests, announcements) while keeping account-required emails</li>
      </ul>

      <SectionHeading>7. Data retention</SectionHeading>
      <ul>
        <li>Account data — kept while your account is active, plus 90 days after deletion (legal/dispute window)</li>
        <li>Admin audit log — 90 days (auto-pruned by MongoDB TTL)</li>
        <li>Password reset tokens — 24 hours (single-use, then deleted)</li>
        <li>Server access logs — 30 days</li>
        <li>Payment records — 7 years (IRS / tax retention requirement)</li>
      </ul>

      <SectionHeading>8. Security</SectionHeading>
      <p>
        We encrypt data in transit with TLS 1.2+, hash passwords with bcrypt, and store
        password-reset tokens as SHA-256 hashes (the plaintext is never stored). We follow
        principle of least privilege — admin actions are logged with field-level diffs.
      </p>
      <p>
        No system is perfectly secure. If you suspect your account has been compromised,
        contact us immediately at the email below.
      </p>

      <SectionHeading>9. Changes to this policy</SectionHeading>
      <p>
        We may update this policy from time to time. Material changes will be communicated by
        email to active subscribers and posted at the top of this page with an updated date.
      </p>

      <SectionHeading>10. Contact</SectionHeading>
      <p>
        Atlas History is operated by Chase A. Casteel. You can reach us by email at{' '}
        <a href="mailto:support@atlashistory.org">support@atlashistory.org</a>.
      </p>
    </LegalPageLayout>
  );
}
